import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import {
  CHART_METRICS,
  FILTER_DEFINITIONS,
  GRANULARITIES,
  LOYALTY_VIEWS,
  PURCHASE_SPLITS,
  type LoyaltyFilters,
  type LoyaltyView,
  filtersError,
} from './model';

const VISIBLE_ROWS = 25;

type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | null> }>;
};

export function loyaltySocietePageView(input: {
  ready: boolean;
  filters: LoyaltyFilters;
  options: Record<string, Array<string>>;
  period: string;
  summary: Record<string, number | null> | null;
  sections: Array<PageSection>;
}): PageView {
  const { filters, options } = input;
  const filterSchema: Record<string, PageField> = {
    view: { type: 'enum', values: LOYALTY_VIEWS.map((view) => view.value) },
    from: { type: 'date' },
    to: { type: 'date' },
    granularity: { type: 'enum', values: GRANULARITIES.map((item) => item.value) },
    purchaseSplit: { type: 'enum', values: PURCHASE_SPLITS.map((item) => item.key) },
    ...Object.fromEntries(
      Object.entries(CHART_METRICS).map(([key, metrics]) => [
        key,
        { type: 'enum', values: metrics.map((item) => item.key) },
      ]),
    ),
    ...Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ views }) => (views as readonly string[]).includes(filters.view)).map(({ key }) => [
        key,
        { type: 'text', multiple: true, values: (options[key] ?? []).filter((value) => !value.includes(',')) },
      ]),
    ),
  };
  return {
    title: 'Loyalty Société',
    ready: input.ready,
    dataMode: 'live Société loyalty tables',
    filters: {
      view: filters.view,
      from: filters.from,
      to: filters.to,
      granularity: filters.granularity,
      purchaseMetric: filters.purchaseMetric,
      purchaseSplit: filters.purchaseSplit,
      earnMetric: filters.earnMetric,
      burnMetric: filters.burnMetric,
      ...Object.fromEntries(
        FILTER_DEFINITIONS.map(({ key }) => [
          key,
          filters.selections[key].join(','),
        ]),
      ),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ key }) => filters.selections[key].length).map(({ key }) => [
        key,
        filters.selections[key],
      ]),
    ),
    period: input.period,
    summary: input.summary,
    visibleSections: input.sections.map((section) => ({
      title: section.title,
      totalRows: section.rows.length,
      rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
      truncated: section.rows.length > VISIBLE_ROWS,
    })),
    definitions: {
      source:
        'lux-analytics-prod: reporting.loyalty_member_point_balance (current member balances), stage.stage_loyalty_point_transactions (daily point ledger) and stage.stage_loyalty_points_items (pay for points). Rebuilt daily.',
      dates:
        'Member balances, tiers and benefits are the current snapshot and ignore the dates. Sign-ups and point activity "yesterday" are the last complete Sydney day. Trends, enrolments and points purchased follow the chosen dates.',
      measures:
        'Points outstanding are base plus bonus points; points earned and burned are Base Points credits and debits; a point is worth A$0.005.',
      missing: 'Unknown is not zero.',
      filters:
        'Filters accept comma-separated listed values; an empty string selects all. Values containing commas cannot be edited here. activeSelections lists every value currently selected.',
    },
  };
}

export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: LoyaltyFilters,
  options: Record<string, Array<string>>,
): Partial<LoyaltyFilters> {
  validatePageFilters(changes, view.filterSchema);
  const next: Partial<LoyaltyFilters> = {};
  if ('view' in changes) {
    next.view = changes.view as LoyaltyView;
  }
  if ('from' in changes) {
    next.from = changes.from as string;
  }
  if ('to' in changes) {
    next.to = changes.to as string;
  }
  if ('granularity' in changes) {
    next.granularity = changes.granularity as LoyaltyFilters['granularity'];
  }
  if ('purchaseSplit' in changes) {
    next.purchaseSplit = changes.purchaseSplit as string;
  }
  for (const key of Object.keys(CHART_METRICS) as Array<keyof typeof CHART_METRICS>) {
    if (key in changes) {
      next[key] = changes[key] as string;
    }
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (!(key in changes)) {
      continue;
    }
    const value = changes[key] as string;
    const listed = options[key] ?? [];
    if (listed.some((item) => item.includes(',') && value.includes(item))) {
      throw new Error(`Values containing commas cannot be set for ${key}.`);
    }
    const values = value ? value.split(',').map((item) => item.trim()) : [];
    if (values.some((item) => !item || !listed.includes(item))) {
      throw new Error(`Invalid ${key}: use values listed on the page.`);
    }
    selections[key] = [...new Set(values)];
    changed = true;
  }
  if (changed) {
    next.selections = selections;
  }
  const error = filtersError({ ...filters, ...next });
  if (error) {
    throw new Error(error);
  }
  return next;
}
